import { createClient } from '@supabase/supabase-js'
import type { EffectiveEntitlement } from '@lobework/contracts/entitlement'

const url = String(import.meta.env.VITE_SUPABASE_URL || '').trim()
const key = String(import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || '').trim()

export const supabase = createClient(url || 'https://invalid.local', key || 'missing', {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true }
})

export const appOrigin = String(import.meta.env.VITE_APP_ORIGIN || window.location.origin).replace(/\/$/, '')

if (typeof window !== 'undefined') {
  supabase.auth.onAuthStateChange((event) => {
    if (event !== 'PASSWORD_RECOVERY') return
    if (window.location.pathname === '/reset-password' || window.location.pathname === '/reset-password.html') return
    const current = new URL(window.location.href)
    const target = new URL('/reset-password', window.location.origin)
    const source = current.searchParams.get('source')
    if (source) target.searchParams.set('source', source)
    window.location.replace(target.pathname + target.search)
  })
}

export async function invoke<T>(name:string, body:Record<string,unknown> = {}):Promise<T>{
  const session = (await supabase.auth.getSession()).data.session
  if(!session) throw new Error('Sign in to continue.')
  const response = await fetch(url + '/functions/v1/' + name, {
    method:'POST',
    headers:{
      Authorization:'Bearer ' + session.access_token,
      apikey:key,
      'Content-Type':'application/json',
      'x-client-info':'lobework-web/1.0.0'
    },
    body:JSON.stringify(body)
  })
  const result = await response.json().catch(()=>({}))
  if(!response.ok) throw new Error(result?.error || result?.message || name + ' failed')
  return result as T
}

export type PortalData={
  profile:any
  subscription:any
  licenses:any[]
  devices:any[]
  releases:any[]
  entitlement:EffectiveEntitlement|null
}

export async function loadPortalData():Promise<PortalData>{
  const user=(await supabase.auth.getUser()).data.user
  if(!user) throw new Error('Sign in to continue.')
  const results=await Promise.all([
    supabase.from('profiles').select('id,full_name,company_name,created_at').eq('id',user.id).maybeSingle(),
    supabase.from('subscriptions').select('*').eq('user_id',user.id).order('created_at',{ascending:false}).limit(1).maybeSingle(),
    supabase.from('licenses').select('*').eq('user_id',user.id).order('created_at',{ascending:false}),
    supabase.from('license_devices').select('*').eq('user_id',user.id).is('deactivated_at',null).order('last_seen_at',{ascending:false}),
    supabase.from('app_releases').select('*').eq('is_published',true).order('released_at',{ascending:false}),
    invoke<EffectiveEntitlement>('get-effective-entitlement').catch(()=>null)
  ])
  return {
    profile:results[0].data,
    subscription:results[1].data,
    licenses:results[2].data || [],
    devices:results[3].data || [],
    releases:results[4].data || [],
    entitlement:results[5]
  }
}
